import type { AxiosInstance } from 'axios';
import { createCrudService, type CrudService } from '../core/crud';
import { unwrap, type ObjectResponse } from '../core/envelope';
import type {
  PrintFormat,
  PrintFormatCreate,
  PrintFormatFilter,
} from '../data/printFormat';

/** Options for resolving the format that should render a document. */
export interface EffectivePrintFormatQuery {
  /** 'invoice' (default) or 'report'. */
  documentType?: string;
  /** Report identifier, when asking for a report-specific format. */
  reportKey?: string;
  storeId?: string;
  /** Labels come back in this language plus the format's default. */
  languageCode?: string;
}

/**
 * Admin CRUD for print themes, plus `effective()` which resolves the format a
 * given document should render with — most-specific-wins, and with its labels
 * and report columns already loaded.
 *
 * Supersedes `invoicePrintFormatService`, which covered invoices only.
 */
export function createPrintFormatService(client: AxiosInstance) {
  const crud = createCrudService<PrintFormat, PrintFormatCreate, PrintFormatFilter>(
    client,
    '/PrintFormat',
    'printFormatId',
  );

  return {
    ...crud,
    /**
     * Returns an empty object (not a 404) when the tenant has configured
     * nothing, so callers apply their built-in defaults rather than branching
     * on status codes.
     */
    effective: async (q: EffectivePrintFormatQuery = {}): Promise<PrintFormat> => {
      const res = await client.post<ObjectResponse<PrintFormat>>(
        '/PrintFormat/effective',
        {},
        {
          params: {
            documentType: q.documentType ?? 'invoice',
            reportKey: q.reportKey,
            storeId: q.storeId,
            languageCode: q.languageCode,
          },
        },
      );
      return unwrap(res.data) ?? {};
    },
  };
}

export type PrintFormatService = CrudService<
  PrintFormat,
  PrintFormatCreate,
  PrintFormatFilter
> & {
  effective: (q?: EffectivePrintFormatQuery) => Promise<PrintFormat>;
};
